// Shared quick-challenge control (plan §8). The time-control picker + colour +
// rated form was copy-pasted in home.ts and player.ts (and would be a third copy
// in the directory); this is the single definition. It renders the inputs only —
// callers add their own submit button / one-tap wiring and call `read()`.

import { t } from "../i18n/index.ts";

export const TIME_CONTROLS: [string, string][] = [
  ["10+0", "Rapid"],
  ["5+0", "Blitz"],
  ["5+3", "Blitz"],
  ["3+2", "Blitz"],
];

export interface ChallengePrefs {
  tc: string;
  color: string;
  rated: boolean;
}

const PREFS_KEY = "chess:challenge-prefs";
const DEFAULT_PREFS: ChallengePrefs = { tc: "5+3", color: "random", rated: false };

/** The member's last-used challenge settings, so one-tap Play needs no picking. */
export function loadPrefs(): ChallengePrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<ChallengePrefs>;
      const tc = TIME_CONTROLS.some(([t]) => t === p.tc) ? p.tc! : DEFAULT_PREFS.tc;
      const color = ["white", "black", "random"].includes(p.color ?? "") ? p.color! : DEFAULT_PREFS.color;
      return { tc, color, rated: Boolean(p.rated) };
    }
  } catch {
    /* fall through to defaults */
  }
  return { ...DEFAULT_PREFS };
}

/** Remember the last-used settings (fired on any real challenge submit). */
export function savePrefs(p: ChallengePrefs): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    /* non-fatal */
  }
}

export interface QuickChallenge {
  element: HTMLElement;
  read(): ChallengePrefs;
}

/** Build the tc-picker + colour + rated inputs, seeded from `init`. */
export function quickChallenge(init: ChallengePrefs = loadPrefs()): QuickChallenge {
  const activeTc = TIME_CONTROLS.findIndex(([t]) => t === init.tc);
  const el = document.createElement("div");
  el.className = "quick-challenge";
  el.innerHTML = `
    <span class="label">${t("qc.timeControl")}</span>
    <div class="tc-picker" role="radiogroup" aria-label="${t("qc.timeControl")}">
      ${TIME_CONTROLS.map(
        ([tc, speed], i) => `
        <label class="tc ${i === activeTc ? "active" : ""}">
          <input type="radio" name="tc" value="${tc}" ${i === activeTc ? "checked" : ""} />
          <strong>${tc}</strong><small>${t(speed === "Rapid" ? "chess.rapid" : "chess.blitz")}</small>
        </label>`,
      ).join("")}
    </div>
    <div class="row">
      <label class="field">
        <span>${t("qc.yourColor")}</span>
        <select name="color">
          <option value="white" ${init.color === "white" ? "selected" : ""}>${t("chess.white")}</option>
          <option value="black" ${init.color === "black" ? "selected" : ""}>${t("chess.black")}</option>
          <option value="random" ${init.color === "random" ? "selected" : ""}>${t("chess.random")}</option>
        </select>
      </label>
      <label class="check"><input type="checkbox" name="rated" ${init.rated ? "checked" : ""} /><span>${t("qc.rated")}</span></label>
    </div>`;

  // Reflect the checked radio in the active style.
  const picker = el.querySelector(".tc-picker")!;
  picker.addEventListener("change", () => {
    picker.querySelectorAll(".tc").forEach((label) => {
      const input = label.querySelector("input") as HTMLInputElement;
      label.classList.toggle("active", input.checked);
    });
  });

  const read = (): ChallengePrefs => ({
    tc: (el.querySelector('input[name="tc"]:checked') as HTMLInputElement | null)?.value ?? init.tc,
    color: (el.querySelector('select[name="color"]') as HTMLSelectElement).value,
    rated: (el.querySelector('input[name="rated"]') as HTMLInputElement).checked,
  });

  return { element: el, read };
}
